// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Injects JSON into the Swagger UI 3 Try-it-out body editor for POST /premium.
 * Instruction used: scope-discipline.instructions.md
 */

export function applyJsonToPremiumTryItOut(jsonText) {
  return applyJsonToPostPath(jsonText, isPremiumPath, 'POST /premium')
}

/**
 * Same injection for the Proposal Submission POST /proposals operation.
 * Instruction used: scope-discipline.instructions.md
 */
export function applyJsonToProposalTryItOut(jsonText) {
  return applyJsonToPostPath(jsonText, isProposalPath, 'POST /proposals')
}

/**
 * LIXI Premium Quote POST /lixi-premium. Flat /premium is never a match.
 * Instruction used: scope-discipline.instructions.md
 */
export function applyJsonToLixiPremiumTryItOut(jsonText) {
  return applyJsonToPostPath(jsonText, isLixiPremiumPath, 'POST /lixi-premium')
}

/**
 * LIXI Proposal Submission POST /lixi-proposal-submission.
 * Instruction used: scope-discipline.instructions.md
 */
export function applyJsonToLixiProposalTryItOut(jsonText) {
  return applyJsonToPostPath(jsonText, isLixiProposalPath, 'POST /lixi-proposal-submission')
}

function applyJsonToPostPath(jsonText, isTargetPath, targetLabel) {
  const root = document.getElementById('swagger-ui-container')
  if (!root) {
    return { ok: false, reason: 'Swagger UI is not available.' }
  }

  const block = findPostBlock(root, isTargetPath)
  if (!block) {
    return { ok: false, reason: targetLabel + ' was not found.' }
  }

  if (!block.classList.contains('is-open')) {
    const summary = block.querySelector('.opblock-summary')
    if (summary) summary.click()
  }

  const tryBtn = block.querySelector('button.try-out__btn')
  if (tryBtn && /try it out/i.test((tryBtn.textContent || '').trim())) {
    tryBtn.click()
  }

  const textarea = block.querySelector('textarea.body-param__text') || block.querySelector('textarea')
  if (!textarea) {
    return { ok: false, reason: 'Request body editor was not found. Copy the JSON and paste it into Try it out.' }
  }

  setNativeTextareaValue(textarea, jsonText)
  return { ok: true }
}

function findPostBlock(root, isTargetPath) {
  const operations = root.querySelectorAll('.opblock')
  for (let i = 0; i < operations.length; i++) {
    const el = operations[i]
    const method = el.querySelector('.opblock-summary-method')
    const path = el.querySelector('.opblock-summary-path')
    const methodText = method ? method.textContent.trim() : ''
    const pathText = path ? path.textContent.replace(/\s/g, '') : ''
    if (methodText === 'POST' && isTargetPath(pathText)) {
      return el
    }
  }
  return null
}

function isPremiumPath(pathText) {
  if (!pathText || pathText.indexOf('lixi') !== -1) return false
  return pathText === '/premium' || /(^|\/)premium$/.test(pathText)
}

function isProposalPath(pathText) {
  if (!pathText || pathText.indexOf('lixi') !== -1) return false
  return pathText === '/proposals' || /(^|\/)proposals$/.test(pathText)
}

function isLixiPremiumPath(pathText) {
  return pathText === '/lixi-premium' || /(^|\/)lixi-premium$/.test(pathText || '')
}

function isLixiProposalPath(pathText) {
  return pathText === '/lixi-proposal-submission' || /(^|\/)lixi-proposal-submission$/.test(pathText || '')
}

function setNativeTextareaValue(textarea, value) {
  const prototype = window.HTMLTextAreaElement && window.HTMLTextAreaElement.prototype
  const descriptor = prototype && Object.getOwnPropertyDescriptor(prototype, 'value')
  if (descriptor && descriptor.set) {
    descriptor.set.call(textarea, value)
  } else {
    textarea.value = value
  }
  textarea.dispatchEvent(new Event('input', { bubbles: true }))
  textarea.dispatchEvent(new Event('change', { bubbles: true }))
}
